import React, { useState, useEffect, useRef } from 'react'
import type { DegradeLevel } from '../../core/export/runtime/performanceMonitor'
import type { ScaleResult } from '../../core/performance/imageScale'
import { formatScaleInfo } from '../../core/performance/imageScale'
import CollapsibleSection from './CollapsibleSection'

interface PerformanceInfoPanelProps {
  getCurrentFPS?: () => number
  getAverageFPS?: () => number
  getDegradeLevel?: () => DegradeLevel
  scaleInfo?: ScaleResult | null
  isAnimating?: boolean
  isLoading?: boolean
  variant?: 'default' | 'compact'
}

const PerformanceInfoPanel: React.FC<PerformanceInfoPanelProps> = ({
  getCurrentFPS,
  getAverageFPS,
  getDegradeLevel,
  scaleInfo,
  isAnimating = false,
  isLoading = false,
  variant = 'default',
}) => {
  const [currentFPS, setCurrentFPS] = useState(0)
  const [averageFPS, setAverageFPS] = useState(0)
  const [degradeLevel, setDegradeLevel] = useState<DegradeLevel>('none')
  const updateIntervalRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isAnimating || !getCurrentFPS) {
      setCurrentFPS(0)
      setAverageFPS(0)
      setDegradeLevel('none')
      return
    }

    updateIntervalRef.current = window.setInterval(() => {
      setCurrentFPS(getCurrentFPS())
      if (getAverageFPS) setAverageFPS(getAverageFPS())
      if (getDegradeLevel) setDegradeLevel(getDegradeLevel())
    }, 500)

    return () => {
      if (updateIntervalRef.current) clearInterval(updateIntervalRef.current)
    }
  }, [isAnimating, getCurrentFPS, getAverageFPS, getDegradeLevel])

  const getFPSLabel = (fps: number) => {
    if (fps >= 50) return '稳定'
    if (fps >= 30) return '一般'
    return '偏低'
  }

  const getDegradeLabel = (level: DegradeLevel) => {
    switch (level) {
      case 'none': return '正常'
      case 'light': return '轻度降级'
      case 'medium': return '中度降级'
      case 'heavy': return '重度降级'
      default: return '未知'
    }
  }

  const hasPerformanceInfo = scaleInfo || isAnimating || isLoading
  if (!hasPerformanceInfo) return null

  if (variant === 'compact') {
    return (
      <div className="xp-floating-panel min-w-[190px] p-2 text-[15px] font-semibold">
        {isLoading && <div className="xp-warning mb-2">正在渲染...</div>}
        {isAnimating && getCurrentFPS && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span>FPS</span>
              <span className="font-bold">{currentFPS}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>评级</span>
              <span>{getFPSLabel(currentFPS)}</span>
            </div>
            {degradeLevel !== 'none' && (
              <div className="flex items-center justify-between">
                <span>降级</span>
                <span>{getDegradeLabel(degradeLevel)}</span>
              </div>
            )}
          </div>
        )}
        {scaleInfo && (
          <div className={`space-y-1 ${isAnimating ? 'mt-2 pt-2 border-t border-[#9b978f]' : ''}`}>
            <div className="flex items-center justify-between">
              <span>字符数</span>
              <span>{scaleInfo.charCount.toLocaleString()}</span>
            </div>
            {scaleInfo.wasScaled && (
              <div className="flex items-center justify-between">
                <span>缩放</span>
                <span>{Math.round(scaleInfo.scaleRatio * 100)}%</span>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <CollapsibleSection title="性能信息" icon="MON" defaultExpanded={true}>
      <div className="space-y-2 text-[15px] font-semibold">
        {isLoading && <div className="xp-warning">正在渲染...</div>}

        {isAnimating && getCurrentFPS && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span>当前 FPS</span>
              <span className="font-bold">{currentFPS}</span>
            </div>
            {getAverageFPS && averageFPS > 0 && (
              <div className="flex items-center justify-between">
                <span>平均 FPS</span>
                <span>{averageFPS}</span>
              </div>
            )}
            {getDegradeLevel && (
              <div className="flex items-center justify-between">
                <span>运行状态</span>
                <span>{getDegradeLabel(degradeLevel)}</span>
              </div>
            )}
            <div className="xp-note">实时性能评级: {getFPSLabel(currentFPS)}</div>
          </div>
        )}

        {scaleInfo && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span>字符数</span>
              <span>{scaleInfo.charCount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>原始尺寸</span>
              <span>{scaleInfo.originalWidth} × {scaleInfo.originalHeight}</span>
            </div>
            {scaleInfo.wasScaled && (
              <>
                <div className="flex items-center justify-between">
                  <span>缩放尺寸</span>
                  <span>{scaleInfo.scaledWidth} × {scaleInfo.scaledHeight}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>缩放比例</span>
                  <span>{Math.round(scaleInfo.scaleRatio * 100)}%</span>
                </div>
                <div className="xp-info">{formatScaleInfo(scaleInfo)}</div>
              </>
            )}
          </div>
        )}

        {!isAnimating && !isLoading && scaleInfo && (
          <div className="xp-note">启用动画后可查看实时 FPS 数据。</div>
        )}
      </div>
    </CollapsibleSection>
  )
}

export default PerformanceInfoPanel
